/*
 This file is part of GNU Taler
 (C) 2022-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import {
  AbsoluteTime,
  TalerError,
  TalerErrorCode,
  TranslatedString,
} from "@gnu-taler/taler-util";
import { useState } from "preact/hooks";
import { InternationalizationAPI } from "../context/translation.js";

export type NotificationMessage = ErrorNotification | InfoNotification;

export interface ErrorNotification {
  type: "error";
  title: TranslatedString;
  description?: TranslatedString[];
  debug?: unknown;
  when: AbsoluteTime;
}

export interface InfoNotification {
  type: "info";
  title: TranslatedString;
  when: AbsoluteTime;
}

export interface Notification {
  message: NotificationMessage;
  acknowledge: () => void;
}

export type ReplaceReturnType<T, NewReturn> = T extends (...args: any[]) => any
  ? (...args: Parameters<T>) => NewReturn
  : never;
type Params<T> = T extends (...args: infer P) => any ? P : never;
type FunctionThatReturnsVoid<T> = (...args: Params<T>) => void;

export function useNotificationHandler() {
  const [notification, setNotification] = useState<Notification[]>([]);

  function pushNotification(message: NotificationMessage): void {
    setNotification((current) => {
      const item: Notification = {
        message,
        acknowledge: () => {
          setNotification((latest) => latest.filter((n) => n !== item));
        },
      };
      return [
        item,
        ...current.filter(
          (existing) =>
            message.type !== "error" || existing.message.type !== "error",
        ),
      ];
    });
  }

  function displayError(
    title: TranslatedString,
    debug: Error | TalerError | unknown | undefined,
    ...description: TranslatedString[]
  ): void {
    pushNotification({
      title,
      type: "error",
      when: AbsoluteTime.now(),
      description,
      debug,
    });
  }

  function displayInfo(title: TranslatedString): void {
    pushNotification({ title, type: "info", when: AbsoluteTime.now() });
  }

  function showError<T extends FunctionThatReturnsVoid<T>>(
    title: TranslatedString,
    handler: ReplaceReturnType<
      T,
      TranslatedString | TranslatedString[] | undefined
    >,
  ): T {
    return ((...args: Parameters<T>): void => {
      const description = handler(...args);
      if (description === undefined) return;
      displayError(
        title,
        (args as unknown[])[0],
        ...(Array.isArray(description) ? description : [description]),
      );
    }) as T;
  }

  function showSuccess<T extends FunctionThatReturnsVoid<T>>(
    handler: ReplaceReturnType<T, TranslatedString | undefined>,
  ): T {
    return ((...args: Parameters<T>): void => {
      const title = handler(...args);
      if (title !== undefined) displayInfo(title);
    }) as T;
  }

  return {
    notification,
    showError,
    showSuccess,
    displayError,
    displayInfo,
    clear: () => setNotification([]),
    clearErrors: () =>
      setNotification((current) =>
        current.filter((item) => item.message.type !== "error"),
      ),
  };
}

function describeErrorResponse(
  i18n: InternationalizationAPI,
  errorResponse: { code?: number; hint?: string },
): TranslatedString | undefined {
  if (!errorResponse.code) return undefined;
  switch (errorResponse.code) {
    case TalerErrorCode.GENERIC_JSON_INVALID:
      return i18n.str`Looks like the JSON in the request was malformed.`;
    default:
      return undefined;
  }
}

function notUndefined<T>(value: T | undefined): value is T {
  return value !== undefined;
}

export function translateTalerError(
  cause: TalerError,
  i18n: InternationalizationAPI,
): TranslatedString[] {
  if (
    cause.hasErrorCode(TalerErrorCode.GENERIC_TIMEOUT) ||
    cause.hasErrorCode(TalerErrorCode.WALLET_HTTP_REQUEST_GENERIC_TIMEOUT)
  ) {
    return [
      i18n.str`The request timed out. Check your connection.`,
      cause.errorDetail.requestUrl
        ? i18n.str`The ${cause.errorDetail.requestMethod} request to ${cause.errorDetail.requestUrl} failed after ${(cause.errorDetail.timeoutMs ?? 0) / 1000} seconds.`
        : undefined,
      cause.errorDetail.when
        ? i18n.str`The last request time is ${AbsoluteTime.stringify(cause.errorDetail.when)}`
        : undefined,
    ].filter(notUndefined);
  }
  if (cause.hasErrorCode(TalerErrorCode.GENERIC_CLIENT_INTERNAL_ERROR)) {
    return [
      i18n.str`The request was cancelled.`,
      i18n.str`The ${cause.errorDetail.requestMethod} request ${cause.errorDetail.requestUrl} failed with code ${cause.errorDetail.httpStatusCode}.`,
      cause.errorDetail.when
        ? i18n.str`The request was made at ${AbsoluteTime.stringify(cause.errorDetail.when)}`
        : undefined,
    ].filter(notUndefined);
  }
  if (cause.hasErrorCode(TalerErrorCode.WALLET_HTTP_REQUEST_THROTTLED)) {
    return [
      i18n.str`Too many requests were made to the server and this action was throttled.`,
      i18n.str`The request "${cause.errorDetail.requestMethod} ${cause.errorDetail.requestUrl}" failed with code ${cause.errorDetail.httpStatusCode}`,
      cause.errorDetail.when
        ? i18n.str`The last request time is ${AbsoluteTime.stringify(cause.errorDetail.when)}`
        : undefined,
    ].filter(notUndefined);
  }
  if (cause.hasErrorCode(TalerErrorCode.WALLET_RECEIVED_MALFORMED_RESPONSE)) {
    return [
      i18n.str`The server's response was malformed.`,
      i18n.str`The response to "${cause.errorDetail.requestMethod} ${cause.errorDetail.requestUrl}" failed with code ${cause.errorDetail.httpStatusCode}`,
      cause.errorDetail.when
        ? i18n.str`The request was made at ${AbsoluteTime.stringify(cause.errorDetail.when)}`
        : undefined,
      cause.errorDetail.contentType
        ? i18n.str`The content type is ${cause.errorDetail.contentType}`
        : undefined,
      cause.errorDetail.validationError
        ? i18n.str`The validation error is "${cause.errorDetail.validationError}"`
        : undefined,
      cause.errorDetail.response
        ? (cause.errorDetail.response as TranslatedString)
        : undefined,
    ].filter(notUndefined);
  }
  if (cause.hasErrorCode(TalerErrorCode.WALLET_NETWORK_ERROR)) {
    return [
      i18n.str`Due to a network problem the request could not be finished.`,
      i18n.str`The ${cause.errorDetail.requestMethod} request to ${cause.errorDetail.requestUrl} failed.`,
      cause.errorDetail.when
        ? i18n.str`The request was made at ${AbsoluteTime.stringify(cause.errorDetail.when)}`
        : undefined,
    ].filter(notUndefined);
  }
  if (cause.hasErrorCode(TalerErrorCode.WALLET_UNEXPECTED_REQUEST_ERROR)) {
    const hint =
      "hint" in cause.errorDetail.errorResponse
        ? cause.errorDetail.errorResponse.hint
        : undefined;
    return [
      i18n.str`The server's response was unexpected. This means the client and the server are not in sync about the protocol.`,
      i18n.str`The ${cause.errorDetail.requestMethod} request to ${cause.errorDetail.requestUrl} failed with code ${cause.errorDetail.httpStatusCode}`,
      cause.errorDetail.when
        ? i18n.str`The request was made at ${AbsoluteTime.stringify(cause.errorDetail.when)}`
        : undefined,
      describeErrorResponse(i18n, cause.errorDetail.errorResponse),
      hint ? i18n.str`And the server says: "${hint}"` : undefined,
    ].filter(notUndefined);
  }
  return [i18n.str`Unexpected error`, cause.message as TranslatedString];
}
